// Ticket API Service - Backend Integration
import { ticketGet, ticketPost, ticketPut, ticketDelete } from '../services/apiClient';
import MockDataService from '../services/MockDataService';

const USE_MOCK = process.env.REACT_APP_USE_MOCK_DATA === 'true' && process.env.NODE_ENV === 'development';

// Helper to wrap response in { data: ... } format for backward compatibility
const wrapResponse = (data) => ({ data });

/**
 * True when hd-ticket ResponseBean indicates success (works with native fetch client, not axios).
 * @param {{ data?: { success?: boolean; status?: number|string } }} wrapped - e.g. return value of assignToTeamLeads
 */
export const isTicketApiSuccess = (wrapped) => {
  const rb = wrapped?.data;
  if (!rb || typeof rb !== 'object') return false;
  if (rb.success === true || rb.success === 'true') return true;
  const st = rb.status;
  return st === 200 || st === 201 || st === '200' || st === '201'
    || (typeof st === 'string' && String(st).toUpperCase() === 'OK');
};

/** Persisted reply from addReply — null unless API succeeded with a server reply id. */
export const extractSavedReplyFromResponse = (wrapped) => {
  if (!isTicketApiSuccess(wrapped)) {
    return null;
  }
  const reply = wrapped?.data?.data;
  if (!reply || typeof reply !== 'object') {
    return null;
  }
  if (reply.replyId == null && reply.id == null) {
    return null;
  }
  return reply;
};

export const ticketService = {

  // ========== TICKET CRUD OPERATIONS ==========
  
  /**
   * Get all tickets (paginated)
   * GET /gateway/api/ticket/list
   */
  getAllTickets: async (params = {}) => {
    if (USE_MOCK) {
      try {
        const mockResponse = await MockDataService.getTicketQueue(params);
        // Transform mock data to backend ResponseBean format
        if (mockResponse.success && mockResponse.data) {
          const tickets = Array.isArray(mockResponse.data) ? mockResponse.data : [];
          const page = params.page || 0;
          const size = params.size || 50;
          const start = page * size;
          const end = start + size;
          const paginatedTickets = tickets.slice(start, end);
          
          return {
            data: {
              success: true,
              message: mockResponse.message || 'Tickets retrieved successfully',
              data: {
                content: paginatedTickets,
                totalElements: tickets.length,
                totalPages: Math.ceil(tickets.length / size),
                pageNumber: page,
                pageSize: size,
                first: page === 0,
                last: end >= tickets.length
              }
            }
          };
        }
        // Fallback to API if mock fails
        const apiResponse = await ticketGet('/gateway/api/ticket/list', params);
        return wrapResponse(apiResponse);
      } catch (error) {
        console.warn('Mock data failed, falling back to API:', error);
        const apiResponse = await ticketGet('/gateway/api/ticket/list', params);
        return wrapResponse(apiResponse);
      }
    }
    try {
      const response = await ticketGet('/gateway/api/ticket/list', params);
      return wrapResponse(response);
    } catch (error) {
      // In production, don't fallback to mock - throw error
      if (process.env.NODE_ENV === 'production') {
        throw error;
      }
      // In development, fallback to mock if enabled
      console.warn('API failed, falling back to mock data:', error);
      const mockResponse = await MockDataService.getTicketQueue(params);
      const tickets = Array.isArray(mockResponse.data) ? mockResponse.data : [];
      const page = params.page || 0;
      const size = params.size || 50;
      const start = page * size;
      const end = start + size;
      const paginatedTickets = tickets.slice(start, end);
      
      return {
        data: {
          success: true,
          message: mockResponse.message || 'Tickets retrieved successfully',
          data: {
            content: paginatedTickets,
            totalElements: tickets.length,
            totalPages: Math.ceil(tickets.length / size),
            pageNumber: page,
            pageSize: size,
            first: page === 0,
            last: end >= tickets.length
          }
        }
      };
    }
  },

  /**
   * Aggregated List of Tickets Report statistics (total + by status + by priority).
   * Same filters as getAllTickets / GET /list; no ticket rows.
   * GET /gateway/api/ticket/list/statistics
   */
  getListTicketsReportStatistics: async (params = {}) => {
    try {
      const response = await ticketGet('/gateway/api/ticket/list/statistics', params);
      return wrapResponse(response);
    } catch (error) {
      if (process.env.NODE_ENV === 'production') {
        throw error;
      }
      console.warn('List tickets report statistics API failed:', error);
      return {
        data: {
          success: true,
          message: 'Statistics unavailable',
          data: {
            totalTickets: 0,
            byStatus: [],
            byPriority: []
          }
        }
      };
    }
  },
  
  /**
   * Get unassigned tickets (paginated)
   * GET /gateway/api/ticket/unassigned
   */
  /**
   * D2.7 — Restricted/Confidential entitled queue (requires can_view on master feature).
   * GET /gateway/api/ticket/restricted-confidential
   */
  getRestrictedConfidentialTickets: async (params = {}) => {
    const response = await ticketGet('/gateway/api/ticket/restricted-confidential', params);
    return wrapResponse(response);
  },

  getRestrictedConfidentialTicketStats: async () => {
    const response = await ticketGet('/gateway/api/ticket/restricted-confidential/stats');
    return wrapResponse(response);
  },

  getUnassignedTickets: async (params = {}) => {
    if (USE_MOCK) {
      try {
        const response = await MockDataService.getTicketQueue(params);
        // Filter to only unassigned tickets
        const unassignedTickets = (response.data || []).filter(t => !t.assignedAgent || t.assignedAgent === 'Unassigned');
        return { data: { success: true, data: { content: unassignedTickets, totalElements: unassignedTickets.length } } };
      } catch (error) {
        console.warn('Mock data failed, trying API:', error);
        try {
          const response = await ticketGet('/gateway/api/ticket/unassigned', params);
          return wrapResponse(response);
        } catch (apiError) {
          return { data: { success: true, data: { content: [], totalElements: 0 } } };
        }
      }
    }
    try {
      const response = await ticketGet('/gateway/api/ticket/unassigned', params);
      return wrapResponse(response);
    } catch (error) {
      if (process.env.NODE_ENV === 'production') {
        throw error;
      }
      console.warn('API failed, falling back to mock data:', error);
      const mockResponse = await MockDataService.getTicketQueue(params);
      const unassignedTickets = (mockResponse.data || []).filter(t => !t.assignedAgent || t.assignedAgent === 'Unassigned');
      return { data: { success: true, data: { content: unassignedTickets, totalElements: unassignedTickets.length } } };
    }
  },
  
  /**
   * Get user's tickets (paginated or all)
   * GET /gateway/api/ticket/my-tickets
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 0) - ignored if fetchAll=true
   * @param {number} params.size - Page size (default: 10) - ignored if fetchAll=true
   * @param {boolean} params.fetchAll - If true, fetches all tickets without pagination (default: false)
   * @param {boolean} params.asRequester - If true, returns tickets user created (portal view). If false/undefined, returns tickets assigned to user (agent view)
   */
  getMyTickets: async (params = {}) => {
    if (USE_MOCK) {
      try {
        const response = await MockDataService.getMyTickets(params);
        // Return in axios response format: { data: { success: true, data: {...} } }
        return { data: response };
      } catch (error) {
        console.warn('Mock data failed, trying API:', error);
        try {
          const response = await ticketGet('/gateway/api/ticket/my-tickets', params);
          return wrapResponse(response);
        } catch (apiError) {
          // If API also fails, return mock data anyway
          const response = await MockDataService.getMyTickets(params);
          return { data: response };
        }
      }
    }
    try {
      const response = await ticketGet('/gateway/api/ticket/my-tickets', params);
      return wrapResponse(response);
    } catch (error) {
      // In production, don't fallback to mock - throw error
      if (process.env.NODE_ENV === 'production') {
        throw error;
      }
      // In development, fallback to mock if enabled
      console.warn('API failed, falling back to mock data:', error);
      const response = await MockDataService.getMyTickets(params);
      return { data: response };
    }
  },
  
  /**
   * Get escalated tickets with role-based filtering
   * GET /gateway/api/ticket/escalated
   * - For Agents: Returns only escalated tickets assigned to the agent
   * - For Admins: Returns all escalated tickets in the tenant
   * - NO MOCK DATA FALLBACK - throws error if API fails
   */
  /**
   * Aggregated statistics for escalated ticket dashboard cards.
   * GET /gateway/api/ticket/escalated/statistics
   */
  getEscalatedStatistics: async (params = {}) => {
    try {
      const { agentId } = params;
      let url = '/gateway/api/ticket/escalated/statistics';
      if (agentId) {
        url += `?agentId=${agentId}`;
      }
      const response = await ticketGet(url);
      return wrapResponse(response);
    } catch (error) {
      console.error('Error fetching escalated statistics:', error);
      throw error;
    }
  },

  getEscalatedTickets: async (params = {}) => {
    try {
      const { page = 0, size = 10, agentId, search, priorityId, startDate, endDate } = params;
      let url = `/gateway/api/ticket/escalated?page=${page}&size=${size}`;
      if (agentId) {
        url += `&agentId=${agentId}`;
      }
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      if (priorityId) {
        url += `&priorityId=${priorityId}`;
      }
      if (startDate) {
        url += `&startDate=${encodeURIComponent(startDate)}`;
      }
      if (endDate) {
        url += `&endDate=${encodeURIComponent(endDate)}`;
      }
      const response = await ticketGet(url);
      return wrapResponse(response);
    } catch (error) {
      console.error('Error fetching escalated tickets:', error);
      // Do NOT fallback to mock data - throw error to prevent showing incorrect data
      throw error;
    }
  },
  
  /**
   * Get statistics for "My Tickets" - counts by status including escalated
   * GET /gateway/api/ticket/my-tickets/statistics
   * @param {Object} params - Query parameters
   * @param {boolean} params.asRequester - Portal customer view (tickets created by user)
   */
  getMyTicketsStatistics: async (params = {}) => {
    try {
      const response = await ticketGet('/gateway/api/ticket/my-tickets/statistics', params);
      return wrapResponse(response);
    } catch (error) {
      console.error('Error fetching my tickets statistics:', error);
      // Return default statistics on error
      return {
        data: {
          success: true,
          data: {
            total: 0,
            open: 0,
            pending: 0,
            'in-progress': 0,
            resolved: 0,
            closed: 0,
            escalated: 0
          }
        }
      };
    }
  },

  /**
   * Get recent activity for current agent dashboard
   * GET /gateway/api/ticket/my-tickets/recent-activity
   */
  getMyRecentActivity: async (limit = 10) => {
    try {
      const response = await ticketGet(`/gateway/api/ticket/my-tickets/recent-activity?limit=${limit}`);
      return wrapResponse(response);
    } catch (error) {
      console.error('Error fetching recent activity:', error);
      throw error;
    }
  },

  /**
   * Tickets resolved or closed today (agent dashboard — no client-side date filtering).
   * GET /gateway/api/ticket/my-tickets/resolved-today
   */
  getResolvedTodayTickets: async () => {
    try {
      const response = await ticketGet('/gateway/api/ticket/my-tickets/resolved-today');
      return wrapResponse(response);
    } catch (error) {
      console.error('Error fetching resolved today tickets:', error);
      throw error;
    }
  },

  /**
   * Get customer rating for the current agent
   * GET /gateway/api/ticket/my-tickets/customer-rating
   */
  getCustomerRating: async () => {
    try {
      const response = await ticketGet('/gateway/api/ticket/my-tickets/customer-rating');
      return wrapResponse(response);
    } catch (error) {
      console.error('Error fetching customer rating:', error);
      throw error;
    }
  },
  
  /**
   * Get ticket by ID
   * GET /gateway/api/ticket/{ticketId}
   */
  getTicketById: async (ticketId) => {
    if (USE_MOCK) {
      try {
        const response = await MockDataService.getTicket(ticketId);
        // MockDataService.getTicket returns { success: true, data: ticket } or just ticket
        // Extract the actual ticket data
        const ticket = response.data || response;
        // Return in axios response format: { data: ticket }
        return { data: ticket };
      } catch (error) {
        console.warn('Mock data failed, trying API:', error);
        try {
          const response = await ticketGet(`/gateway/api/ticket/${ticketId}`);
          return wrapResponse(response);
        } catch (apiError) {
          // If API also fails, return mock data anyway
          const response = await MockDataService.getTicket(ticketId);
          const ticket = response.data || response;
          return { data: ticket };
        }
      }
    }
    try {
      const response = await ticketGet(`/gateway/api/ticket/${ticketId}`);
      return wrapResponse(response);
    } catch (error) {
      // In production, don't fallback to mock - throw error
      if (process.env.NODE_ENV === 'production') {
        throw error;
      }
      // In development, fallback to mock if enabled
      console.warn('API failed, falling back to mock data:', error);
      const response = await MockDataService.getTicket(ticketId);
      const ticket = response.data || response;
      return { data: ticket };
    }
  },
  
  /**
   * Create new ticket
   * POST /gateway/api/ticket/create
   */
  createTicket: async (data) => {
    const response = await ticketPost('/gateway/api/ticket/create', data);
    return wrapResponse(response);
  },

  /**
   * Tenant-scoped customer lookup by email (workforce create on behalf).
   * GET /gateway/api/ticket/customers/by-email?email=
   */
  lookupCustomerByEmail: async (email) => {
    const response = await ticketGet('/gateway/api/ticket/customers/by-email', { email });
    return wrapResponse(response);
  },

  /**
   * Phase 1 Inquiry masters for Create Ticket (authenticated users).
   * GET /gateway/api/ticket/inquiry-masters/*
   */
  getInquiryTypesForCreate: async () => {
    const response = await ticketGet('/gateway/api/ticket/inquiry-masters/types');
    const data = response?.data?.data ?? response?.data ?? response;
    return Array.isArray(data) ? data : [];
  },

  getInquiryCategoriesForCreate: async (inquiryTypeId) => {
    const response = await ticketGet('/gateway/api/ticket/inquiry-masters/categories', {
      inquiryTypeId
    });
    const data = response?.data?.data ?? response?.data ?? response;
    return Array.isArray(data) ? data : [];
  },

  getInquirySubCategoriesForCreate: async (inquiryCategoryId) => {
    const response = await ticketGet('/gateway/api/ticket/inquiry-masters/sub-categories', {
      inquiryCategoryId
    });
    const data = response?.data?.data ?? response?.data ?? response;
    return Array.isArray(data) ? data : [];
  },

  getInquiryPriorityLevelsForCreate: async () => {
    const response = await ticketGet('/gateway/api/ticket/inquiry-masters/priority-levels');
    const data = response?.data?.data ?? response?.data ?? response;
    return Array.isArray(data) ? data : [];
  },

  /**
   * Level 1 Responsible Role for Assign Inquiry when Inquiry Priority is selected.
   * GET /gateway/api/ticket/inquiry-masters/level1-responsible-role
   */
  getLevel1ResponsibleRoleForAssign: async (inquiryCategoryId, inquirySubCategoryId, priorityLevelId) => {
    const response = await ticketGet('/gateway/api/ticket/inquiry-masters/level1-responsible-role', {
      inquiryCategoryId,
      inquirySubCategoryId,
      priorityLevelId,
    });
    return response?.data?.data ?? response?.data ?? response;
  },
  
  /**
   * Update ticket
   * POST /gateway/api/ticket/{ticketId}/update
   */
  updateTicket: async (ticketId, data) => {
    const response = await ticketPost(`/gateway/api/ticket/${ticketId}/update`, data);
    return wrapResponse(response);
  },

  /**
   * Queue-only confidentiality level (requires master feature can_update).
   * POST /gateway/api/ticket/{ticketId}/confidentiality-level
   */
  updateTicketConfidentialityLevel: async (ticketId, confidentialityLevel) => {
    const response = await ticketPost(`/gateway/api/ticket/${ticketId}/confidentiality-level`, {
      confidentialityLevel,
    });
    return wrapResponse(response);
  },
  
  /**
   * Delete ticket (soft delete)
   * DELETE /gateway/api/ticket/{ticketId}
   */
  deleteTicket: async (ticketId) => {
    const response = await ticketDelete(`/gateway/api/ticket/${ticketId}`);
    return wrapResponse(response);
  },
  
  /**
   * Assign ticket to user
   * POST /gateway/api/ticket/{ticketId}/assign
   * @param {number} ticketId - The ticket ID to assign
   * @param {number} assigneeId - The user ID to assign the ticket to
   * @param {number} requesterId - The user ID who created the ticket (for FCM notification)
   */
  assignTicket: async (ticketId, assigneeId, requesterId = null, priorityId = null) => {
    // Build query params - include requesterId if provided for backend FCM notification
    let queryParams = `assigneeId=${assigneeId}`;
    if (requesterId) {
      queryParams += `&requesterId=${requesterId}`;
    }
    if (priorityId != null && priorityId !== '') {
      queryParams += `&priorityId=${priorityId}`;
    }
    const response = await ticketPost(`/gateway/api/ticket/${ticketId}/assign?${queryParams}`, {});
    return wrapResponse(response);
  },

  /**
   * Bulk assign tickets
   * PUT /gateway/api/ticket/bulk/assign
   */
  bulkAssignTickets: async (ticketIds, assigneeId, priorityId) => {
    const response = await ticketPut(`/gateway/api/ticket/bulk/assign`, {
      ticketIds,
      assigneeId,
      priorityId,
    });
    return wrapResponse(response);
  },

  /**
   * Escalate ticket to team leads (agent queues for team lead assignment)
   * POST /gateway/api/ticket/{ticketId}/assign-to-team-leads?agentId={agentId}
   */
  assignTicketToTeamLeads: async (ticketId, agentId) => {
    const response = await ticketPost(
      `/gateway/api/ticket/${ticketId}/assign-to-team-leads?agentId=${encodeURIComponent(agentId)}`,
      {}
    );
    return wrapResponse(response);
  },
  
  /**
   * Assign ticket to team leads (all team leads of the agent's team)
   * POST /gateway/api/ticket/{ticketId}/assign-to-team-leads?agentId={agentId}
   * @param {number} ticketId - The ticket ID to assign
   * @param {number} agentId - The agent ID who is requesting team lead assignment
   */
  assignToTeamLeads: async (ticketId, agentId) => {
    try {
      const response = await ticketPost(
        `/gateway/api/ticket/${ticketId}/assign-to-team-leads?agentId=${encodeURIComponent(String(agentId))}`,
        {}
      );
      return wrapResponse(response);
    } catch (error) {
      console.error('Error assigning ticket to team leads:', error);
      throw error;
    }
  },

  /**
   * C3 — Manual advance one Inquiry Escalation Matrix tier (FR-ESC-03).
   * POST /gateway/api/ticket/{ticketId}/inquiry/escalate
   */
  manualInquiryMatrixEscalate: async (ticketId, reason) => {
    const response = await ticketPost(
      `/gateway/api/ticket/${ticketId}/inquiry/escalate`,
      { reason }
    );
    return wrapResponse(response);
  },
  
  /**
   * Update ticket status
   * POST /gateway/api/ticket/{ticketId}/status
   */
  updateTicketStatus: async (ticketId, statusId) => {
    const response = await ticketPost(`/gateway/api/ticket/${ticketId}/status?statusId=${statusId}`, {});
    return wrapResponse(response);
  },

  /**
   * Update ticket priority only (Team Lead / Admin)
   * POST /gateway/api/ticket/{ticketId}/priority?priorityId={priorityId}
   */
  updateTicketPriority: async (ticketId, priorityId) => {
    const response = await ticketPost(
      `/gateway/api/ticket/${ticketId}/priority?priorityId=${encodeURIComponent(priorityId)}`,
      {}
    );
    return wrapResponse(response);
  },

  /**
   * Resolve a ticket (creates resolution reply and updates status to resolved)
   * POST /gateway/api/ticket/{ticketId}/resolve
   * @param {number} ticketId - The ticket ID to resolve
   * @param {object} resolutionData - Resolution data including content, summary, rootCause, etc.
   */
  resolveTicket: async (ticketId, resolutionData) => {
    try {
      const response = await ticketPost(`/gateway/api/ticket/${ticketId}/resolve`, resolutionData);
      return wrapResponse(response);
    } catch (error) {
      console.error('Error resolving ticket:', error);
      throw error;
    }
  },

  /**
   * Phase 6 — pending Inquiry Supervisor Approvals
   * GET /gateway/api/ticket/supervisor-approvals/pending
   * @param {Object} params - page, size, search, priorityId, categoryId
   */
  getPendingSupervisorApprovals: async (params = {}) => {
    const query = {
      page: params.page ?? 0,
      size: params.size ?? 10,
    };
    if (params.search?.trim()) {
      query.search = params.search.trim();
    }
    if (params.priorityId != null && params.priorityId !== '' && params.priorityId !== 'all') {
      const parsed = Number(params.priorityId);
      if (Number.isFinite(parsed)) query.priorityId = parsed;
    }
    if (params.categoryId != null && params.categoryId !== '' && params.categoryId !== 'all') {
      const parsed = Number(params.categoryId);
      if (Number.isFinite(parsed)) query.categoryId = parsed;
    }
    const response = await ticketGet('/gateway/api/ticket/supervisor-approvals/pending', query);
    return response?.data?.data ?? response?.data ?? response;
  },

  /**
   * Phase 6 — Supervisor Approve / Reject history (paginated)
   * GET /gateway/api/ticket/supervisor-approvals/history
   * @param {Object} params - page, size, search, priorityId, categoryId, changeType, startDate, endDate
   */
  getSupervisorApprovalHistory: async (params = {}) => {
    const query = {
      page: params.page ?? 0,
      size: params.size ?? 10,
    };
    if (params.search?.trim()) {
      query.search = params.search.trim();
    }
    if (params.priorityId != null && params.priorityId !== '' && params.priorityId !== 'all') {
      const parsed = Number(params.priorityId);
      if (Number.isFinite(parsed)) query.priorityId = parsed;
    }
    if (params.categoryId != null && params.categoryId !== '' && params.categoryId !== 'all') {
      const parsed = Number(params.categoryId);
      if (Number.isFinite(parsed)) query.categoryId = parsed;
    }
    if (params.changeType && params.changeType !== 'all') {
      query.changeType = params.changeType;
    }
    if (params.startDate) query.startDate = params.startDate;
    if (params.endDate) query.endDate = params.endDate;
    const response = await ticketGet('/gateway/api/ticket/supervisor-approvals/history', query);
    return response?.data?.data ?? response?.data ?? response;
  },

  /**
   * Phase 7 — Supervisor approves Inquiry resolution (starts Grace Period)
   * POST /gateway/api/ticket/{ticketId}/supervisor-approval/approve
   */
  approveSupervisorResolution: async (ticketId) => {
    const response = await ticketPost(
      `/gateway/api/ticket/${ticketId}/supervisor-approval/approve`,
      {}
    );
    return wrapResponse(response);
  },

  /**
   * Phase 6 — Supervisor rejects Inquiry resolution (remarks required)
   * POST /gateway/api/ticket/{ticketId}/supervisor-approval/reject
   */
  rejectSupervisorResolution: async (ticketId, remarks) => {
    const response = await ticketPost(
      `/gateway/api/ticket/${ticketId}/supervisor-approval/reject`,
      { remarks }
    );
    return wrapResponse(response);
  },

  /**
   * Phase 7 — Requester accepts resolution (optional feedback) and closes ticket
   * POST /gateway/api/ticket/{ticketId}/accept-resolution
   */
  acceptResolution: async (ticketId, data = {}) => {
    const response = await ticketPost(
      `/gateway/api/ticket/${ticketId}/accept-resolution`,
      data || {}
    );
    return wrapResponse(response);
  },

  /**
   * Phase 10 — Requester reopens ticket during active Grace Period
   * POST /gateway/api/ticket/{ticketId}/reopen-during-grace
   */
  reopenDuringGrace: async (ticketId) => {
    const response = await ticketPost(
      `/gateway/api/ticket/${ticketId}/reopen-during-grace`,
      {}
    );
    return wrapResponse(response);
  },

  // ========== TICKET REPLY OPERATIONS ==========
  
  /**
   * Add reply to ticket
   * POST /gateway/api/ticket/{ticketId}/reply
   */
  addReply: async (ticketId, data) => {
    if (USE_MOCK) {
      try {
        // Get ticket to add reply to
        const ticketResponse = await MockDataService.getTicket(ticketId);
        const ticket = ticketResponse.data || ticketResponse;
        
        // Determine if this is an internal note
        const isInternal = data.isInternal === true;
        
        // Create new reply
        const newReply = {
          id: `reply_${ticketId}_${Date.now()}`,
          content: data.content || '',
          message: data.content || '',
          createdBy: { name: 'Current Agent' },
          author: { name: 'Current Agent' },
          createdAt: new Date().toISOString(),
          timestamp: new Date().toISOString(),
          type: isInternal ? 'internal' : 'comment',
          isInternal: isInternal
        };
        
        // Add reply to ticket's comments/communications
        if (!ticket.comments) ticket.comments = [];
        if (!ticket.communications) ticket.communications = [];
        ticket.comments.push(newReply);
        ticket.communications.push(newReply);
        
        return wrapResponse(newReply);
      } catch (error) {
        console.warn('Mock data failed, trying API:', error);
        const response = await ticketPost(`/gateway/api/ticket/${ticketId}/reply`, data);
        return wrapResponse(response);
      }
    }
    const response = await ticketPost(`/gateway/api/ticket/${ticketId}/reply`, data);
    return wrapResponse(response);
  },
  
  /**
   * Get all replies for ticket
   * GET /gateway/api/ticket/{ticketId}/reply
   */
  getReplies: async (ticketId) => {
    if (USE_MOCK) {
      try {
        const ticketResponse = await MockDataService.getTicket(ticketId);
        const ticket = ticketResponse.data || ticketResponse;
        // Extract replies from ticket (comments or communications)
        const replies = ticket.comments || ticket.communications || ticket.replies || [];
        // Transform to reply format
        const formattedReplies = replies.map((reply, idx) => ({
          id: reply.id || `reply_${ticketId}_${idx}`,
          content: reply.content || reply.message || '',
          message: reply.content || reply.message || '',
          createdBy: reply.author || reply.createdBy || { name: 'System' },
          author: reply.author || reply.createdBy || { name: 'System' },
          createdAt: reply.createdAt || reply.timestamp || new Date().toISOString(),
          timestamp: reply.createdAt || reply.timestamp || new Date().toISOString(),
          type: reply.type || 'comment'
        }));
        return { data: formattedReplies };
      } catch (error) {
        console.warn('Mock data failed, trying API:', error);
        try {
          const response = await ticketGet(`/gateway/api/ticket/${ticketId}/reply`);
          return wrapResponse(response);
        } catch (apiError) {
          // If API also fails, return empty array
          return { data: [] };
        }
      }
    }
    try {
      const response = await ticketGet(`/gateway/api/ticket/${ticketId}/reply`);
      // Transform backend response to frontend format
      // Backend sends: { replyId, userId, userName, content, ... }
      // Frontend expects: { id, createdBy: { name }, author: { name }, content, ... }
      const replies = response?.data || response || [];
      const formattedReplies = Array.isArray(replies) ? replies.map((reply) => ({
        id: reply.replyId || reply.id,
        replyId: reply.replyId,
        userId: reply.userId,
        content: reply.content || reply.message || '',
        message: reply.content || reply.message || '',
        createdBy: { 
          name: reply.userName || reply.createdBy?.name || reply.author?.name || `User ${reply.userId}` 
        },
        author: { 
          name: reply.userName || reply.author?.name || reply.createdBy?.name || `User ${reply.userId}` 
        },
        createdAt: reply.createdAt || reply.timestamp || new Date().toISOString(),
        timestamp: reply.createdAt || reply.timestamp || new Date().toISOString(),
        type: reply.replyType || reply.type || 'comment',
        isInternal: reply.isInternal || false
      })) : [];
      return { data: formattedReplies };
    } catch (error) {
      // In production, don't fallback to mock - throw error
      if (process.env.NODE_ENV === 'production') {
        throw error;
      }
      // In development, fallback to mock if enabled
      console.warn('API failed, falling back to mock data:', error);
      const ticketResponse = await MockDataService.getTicket(ticketId);
      const ticket = ticketResponse.data || ticketResponse;
      const replies = ticket.comments || ticket.communications || ticket.replies || [];
      const formattedReplies = replies.map((reply, idx) => ({
        id: reply.id || `reply_${ticketId}_${idx}`,
        content: reply.content || reply.message || '',
        message: reply.content || reply.message || '',
        createdBy: reply.author || reply.createdBy || { name: 'System' },
        author: reply.author || reply.createdBy || { name: 'System' },
        createdAt: reply.createdAt || reply.timestamp || new Date().toISOString(),
        timestamp: reply.createdAt || reply.timestamp || new Date().toISOString(),
        type: reply.type || 'comment'
      }));
      return { data: formattedReplies };
    }
  },
  
  /**
   * Update reply
   * PUT /gateway/api/ticket/{ticketId}/reply/{replyId}
   */
  updateReply: async (ticketId, replyId, data) => {
    if (USE_MOCK) {
      try {
        // Get ticket to update reply in
        const ticketResponse = await MockDataService.getTicket(ticketId);
        const ticket = ticketResponse.data || ticketResponse;
        
        // Find and update reply
        const replies = ticket.comments || ticket.communications || [];
        const replyIndex = replies.findIndex(r => r.id === replyId);
        
        if (replyIndex !== -1) {
          replies[replyIndex] = {
            ...replies[replyIndex],
            content: data.content || replies[replyIndex].content,
            message: data.content || replies[replyIndex].message,
            updatedAt: new Date().toISOString()
          };
          return { data: replies[replyIndex] };
        }
        
        // If reply not found, return updated mock reply
        return { 
          data: {
            id: replyId,
            content: data.content || '',
            createdBy: { name: 'Current Agent' },
            updatedAt: new Date().toISOString()
          }
        };
      } catch (error) {
        console.warn('Mock data failed, trying API:', error);
        try {
          const response = await ticketPut(`/gateway/api/ticket/${ticketId}/reply/${replyId}`, data);
          return wrapResponse(response);
        } catch (apiError) {
          return { 
            data: {
              id: replyId,
              content: data.content || '',
              updatedAt: new Date().toISOString()
            }
          };
        }
      }
    }
    try {
      const response = await ticketPut(`/gateway/api/ticket/${ticketId}/reply/${replyId}`, data);
      return wrapResponse(response);
    } catch (error) {
      // In production, don't fallback to mock - throw error
      if (process.env.NODE_ENV === 'production') {
        throw error;
      }
      // In development, fallback to mock if enabled
      console.warn('API failed, falling back to mock data:', error);
      return { 
        data: {
          id: replyId,
          content: data.content || '',
          updatedAt: new Date().toISOString()
        }
      };
    }
  },
  
  /**
   * Delete reply
   * DELETE /gateway/api/ticket/{ticketId}/reply/{replyId}
   */
  deleteReply: async (ticketId, replyId) => {
    if (USE_MOCK) {
      try {
        // Mock delete - just return success
        return { data: { id: replyId, deleted: true } };
      } catch (error) {
        console.warn('Mock data failed, trying API:', error);
        try {
          const response = await ticketDelete(`/gateway/api/ticket/${ticketId}/reply/${replyId}`);
          return wrapResponse(response);
        } catch (apiError) {
          return { data: { id: replyId, deleted: true } };
        }
      }
    }
    try {
      const response = await ticketDelete(`/gateway/api/ticket/${ticketId}/reply/${replyId}`);
      return wrapResponse(response);
    } catch (error) {
      // In production, don't fallback to mock - throw error
      if (process.env.NODE_ENV === 'production') {
        throw error;
      }
      // In development, fallback to mock if enabled
      console.warn('API failed, falling back to mock data:', error);
      return { data: { id: replyId, deleted: true } };
    }
  },

  // ========== TICKET ATTACHMENT OPERATIONS ==========
  
  /**
   * Upload attachment to ticket
   * POST /gateway/api/ticket/{ticketId}/attachment
   */
  uploadAttachment: async (ticketId, file) => {
    if (USE_MOCK) {
      try {
        // Create mock attachment
        const newAttachment = {
          id: `att_${ticketId}_${Date.now()}`,
          name: file.name || 'attachment',
          fileName: file.name || 'attachment',
          size: file.size || 0,
          type: file.type || 'application/octet-stream',
          url: '#',
          createdAt: new Date().toISOString()
        };
        return { data: newAttachment };
      } catch (error) {
        console.warn('Mock data failed, trying API:', error);
        try {
          const formData = new FormData();
          formData.append('file', file);
          const response = await ticketPost(`/gateway/api/ticket/${ticketId}/attachment`, formData);
          return wrapResponse(response);
        } catch (apiError) {
          return { 
            data: {
              id: `att_${ticketId}_${Date.now()}`,
              name: file.name || 'attachment',
              size: file.size || 0
            }
          };
        }
      }
    }
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await ticketPost(`/gateway/api/ticket/${ticketId}/attachment`, formData);
      return wrapResponse(response);
    } catch (error) {
      // In production, don't fallback to mock - throw error
      if (process.env.NODE_ENV === 'production') {
        throw error;
      }
      // In development, fallback to mock if enabled
      console.warn('API failed, falling back to mock data:', error);
      return { 
        data: {
          id: `att_${ticketId}_${Date.now()}`,
          name: file.name || 'attachment',
          size: file.size || 0
        }
      };
    }
  },
  
  /**
   * Upload attachment to reply
   * Uses file service API: /gateway/file-service/jwt/file/upload
   * @param {number} ticketId - Ticket ID (used as reg_id, same as ticket attachments)
   * @param {number} replyId - Reply ID (for reference, but ticketId is used for file association)
   * @param {File} file - File to upload
   */
  uploadReplyAttachment: async (ticketId, replyId, file) => {
    // Import fileService dynamically to avoid circular dependencies
    const { fileService } = await import('./fileService');
    
    // Use the same file service API as ticket attachments
    // Use ticketId as reg_id (same as ticket attachments) so the file is properly associated with the ticket
    // The screenName can be 'REPLY' to differentiate, but reg_id must be ticketId
    return await fileService.uploadFile(file, {
      reg_id: parseInt(ticketId),
      fileTypeId: 1,
      isActive: true,
      screenName: 'REPLY'
    });
  },
  
  
  /**
   * Get all attachments for ticket
   * GET /gateway/api/ticket/get/ticketfiles/{ticketId}
   */
  getAttachments: async (ticketId) => {
    const DOMAIN_URL = 'https://helpdeskdev.dhanushinfotech.com';
    
    if (USE_MOCK) {
      try {
        const ticketResponse = await MockDataService.getTicket(ticketId);
        const ticket = ticketResponse.data || ticketResponse;
        // Extract attachments from ticket
        const attachments = ticket.attachments || [];
        // Transform to attachment format
        const formattedAttachments = attachments.map((att, idx) => ({
          id: att.id || `att_${ticketId}_${idx}`,
          name: att.name || att.fileName || 'attachment',
          fileName: att.name || att.fileName || 'attachment',
          size: att.size || 0,
          type: att.type || 'application/octet-stream',
          url: att.url || '#',
          createdAt: att.createdAt || new Date().toISOString()
        }));
        return { data: formattedAttachments };
      } catch (error) {
        console.warn('Mock data failed, trying API:', error);
        try {
          const response = await ticketGet(`/gateway/api/ticket/get/ticketfiles/${ticketId}`);
          return wrapResponse(response);
        } catch (apiError) {
          // If API also fails, return empty array
          return { data: [] };
        }
      }
    }
    try {
      const response = await ticketGet(`/gateway/api/ticket/get/ticketfiles/${ticketId}`);
      // Backend returns ResponseBean: { status, message, data, success, timestamp }
      // data: TicketFileResponseDTO[] — id, fileName, documentPath, createdOn, createdBy, uploaderName, uploaderRole, ticketNumber
      let attachmentsData = [];

      if (response?.data?.data && Array.isArray(response.data.data)) {
        attachmentsData = response.data.data;
      } else if (Array.isArray(response?.data)) {
        attachmentsData = response.data;
      }

      const origin =
        typeof window !== 'undefined' && window.location?.hostname === 'localhost'
          ? DOMAIN_URL
          : typeof window !== 'undefined'
            ? window.location.origin
            : DOMAIN_URL;

      const formattedAttachments = attachmentsData.map((file, idx) => {
        const documentPath = file.documentPath || file.document_path || '';
        const pathTail = documentPath.split('/').pop()?.split('?')[0] || `attachment_${idx}`;
        const displayName = String(file.fileName || file.file_name || '').trim() || pathTail;
        const fullUrl = documentPath
          ? `${origin}${documentPath.startsWith('/') ? '' : '/'}${documentPath}`
          : '#';
        const createdOn = file.createdOn ?? file.created_on ?? null;

        return {
          id: file.id != null && file.id !== '' ? file.id : `att_${ticketId}_${idx}`,
          name: displayName,
          fileName: displayName,
          size: 0,
          type: 'application/octet-stream',
          url: fullUrl,
          documentPath,
          document_path: documentPath,
          ticketNumber: file.ticketNumber ?? file.ticket_number,
          createdOn,
          createdAt: createdOn,
          createdBy: file.createdBy ?? file.created_by ?? null,
          uploaderName: file.uploaderName ?? file.uploader_name ?? null,
          uploaderRole: file.uploaderRole ?? file.uploader_role ?? null
        };
      });

      return { data: formattedAttachments };
    } catch (error) {
      // In production, don't fallback to mock - throw error
      if (process.env.NODE_ENV === 'production') {
        throw error;
      }
      // In development, fallback to mock if enabled
      console.warn('API failed, falling back to mock data:', error);
      const ticketResponse = await MockDataService.getTicket(ticketId);
      const ticket = ticketResponse.data || ticketResponse;
      const attachments = ticket.attachments || [];
      const formattedAttachments = attachments.map((att, idx) => ({
        id: att.id || `att_${ticketId}_${idx}`,
        name: att.name || att.fileName || 'attachment',
        fileName: att.name || att.fileName || 'attachment',
        size: att.size || 0,
        type: att.type || 'application/octet-stream',
        url: att.url || '#',
        createdAt: att.createdAt || new Date().toISOString()
      }));
      return { data: formattedAttachments };
    }
  },
  
  /**
   * Download attachment
   * GET /gateway/api/ticket/attachment/{attachmentId}/download
   */
  downloadAttachment: async (attachmentId) => {
    if (USE_MOCK) {
      try {
        // Create a mock blob for download
        const blob = new Blob(['Mock attachment content'], { type: 'text/plain' });
        return { data: blob };
      } catch (error) {
        console.warn('Mock data failed, trying API:', error);
        try {
          // Note: blob responses need special handling
          const response = await ticketGet(`/gateway/api/ticket/attachment/${attachmentId}/download`);
          return wrapResponse(response);
        } catch (apiError) {
          const blob = new Blob(['Mock attachment content'], { type: 'text/plain' });
          return { data: blob };
        }
      }
    }
    try {
      // Note: blob responses need special handling
      const response = await ticketGet(`/gateway/api/ticket/attachment/${attachmentId}/download`);
      return wrapResponse(response);
    } catch (error) {
      // In production, don't fallback to mock - throw error
      if (process.env.NODE_ENV === 'production') {
        throw error;
      }
      // In development, fallback to mock if enabled
      console.warn('API failed, falling back to mock data:', error);
      const blob = new Blob(['Mock attachment content'], { type: 'text/plain' });
      return { data: blob };
    }
  },
  
  /**
   * Delete attachment
   * DELETE /gateway/api/ticket/attachment/{attachmentId}
   */
  deleteAttachment: async (attachmentId) => {
    if (USE_MOCK) {
      try {
        // Mock delete - just return success
        return { data: { id: attachmentId, deleted: true } };
      } catch (error) {
        console.warn('Mock data failed, trying API:', error);
        try {
          const response = await ticketDelete(`/gateway/api/ticket/attachment/${attachmentId}`);
          return wrapResponse(response);
        } catch (apiError) {
          return { data: { id: attachmentId, deleted: true } };
        }
      }
    }
    try {
      const response = await ticketDelete(`/gateway/api/ticket/attachment/${attachmentId}`);
      return wrapResponse(response);
    } catch (error) {
      // In production, don't fallback to mock - throw error
      if (process.env.NODE_ENV === 'production') {
        throw error;
      }
      // In development, fallback to mock if enabled
      console.warn('API failed, falling back to mock data:', error);
      return { data: { id: attachmentId, deleted: true } };
    }
  },

  // ========== TICKET REFERENCE DATA ==========
  
  /**
   * Get ticket types
   * GET /gateway/api/ticket/types
   */
  getTicketTypes: async () => {
    const response = await ticketGet('/gateway/api/ticket/types');
    return wrapResponse(response);
  },
  
  /**
   * Get all ticket categories (including inactive)
   * GET /gateway/api/ticket/categories/all
   */
  getTicketCategories: async (params = {}) => {
    try {
      // Use /all endpoint to get all categories (including inactive)
      const response = await ticketGet('/gateway/api/ticket/categories/all', params);
      
      // Extract categories data
      let categoriesData = [];
      if (response?.data?.success && response?.data?.data) {
        categoriesData = Array.isArray(response.data.data) ? response.data.data : [];
      } else if (Array.isArray(response?.data)) {
        categoriesData = response.data;
      }
      
      // Add default "Select" option as first item
      const defaultSelectOption = {
        categoryId: null,
        categoryName: 'Select a category...',
        description: '',
        isActive: true
      };
      
      // Prepend default option
      const categoriesWithDefault = [defaultSelectOption, ...categoriesData];
      
      // Return in same format
      return {
        data: {
          success: true,
          message: 'Categories retrieved successfully',
          data: categoriesWithDefault
        }
      };
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  },
  
  /**
   * Get active ticket categories only
   * GET /gateway/api/ticket/categories
   */
  getActiveTicketCategories: async (params = {}) => {
    try {
      const response = await ticketGet('/gateway/api/ticket/categories', params);
      
      // Extract categories data
      let categoriesData = [];
      if (response?.data?.success && response?.data?.data) {
        categoriesData = Array.isArray(response.data.data) ? response.data.data : [];
      } else if (Array.isArray(response?.data)) {
        categoriesData = response.data;
      }
      
      // Add default "Select" option as first item
      const defaultSelectOption = {
        categoryId: null,
        categoryName: 'Select a category...',
        description: '',
        isActive: true
      };
      
      // Prepend default option
      const categoriesWithDefault = [defaultSelectOption, ...categoriesData];
      
      // Return in same format
      return {
        data: {
          success: true,
          message: 'Categories retrieved successfully',
          data: categoriesWithDefault
        }
      };
    } catch (error) {
      console.error('Error fetching active categories:', error);
      throw error;
    }
  },
  
  /**
   * Get category by ID
   * GET /gateway/api/ticket/categories/{categoryId}
   */
  getTicketCategoryById: async (categoryId) => {
    try {
      const response = await ticketGet(`/gateway/api/ticket/categories/${categoryId}`);
      return wrapResponse(response);
    } catch (error) {
      console.error('Error fetching category:', error);
      throw error;
    }
  },
  
  /**
   * Create a new category
   * POST /gateway/api/ticket/categories
   */
  createTicketCategory: async (categoryData) => {
    try {
      const response = await ticketPost('/gateway/api/ticket/categories', categoryData);
      return wrapResponse(response);
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  },
  
  /**
   * Update an existing category
   * PUT /gateway/api/ticket/categories/{categoryId}
   */
  updateTicketCategory: async (categoryId, categoryData) => {
    try {
      const response = await ticketPut(`/gateway/api/ticket/categories/${categoryId}`, categoryData);
      return wrapResponse(response);
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  },
  
  /**
   * Delete a category (soft delete)
   * DELETE /gateway/api/ticket/categories/{categoryId}
   */
  deleteTicketCategory: async (categoryId) => {
    try {
      const response = await ticketDelete(`/gateway/api/ticket/categories/${categoryId}`);
      return wrapResponse(response);
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  },
  
  /**
   * Get ticket priorities
   * GET /gateway/api/ticket/reference/priorities
   */
  getTicketPriorities: async () => {
    try {
      const response = await ticketGet('/gateway/api/ticket/reference/priorities');
      
      // Backend returns: { data: { success: true, message: "...", data: [...] } }
      let prioritiesData = [];
      
      if (response?.data?.data) {
        prioritiesData = Array.isArray(response.data.data) 
          ? response.data.data 
          : [];
      } else if (Array.isArray(response?.data)) {
        prioritiesData = response.data;
      }
      
      // Transform backend format to frontend format
      const transformedPriorities = prioritiesData.map((pri) => ({
        priorityId: pri.priorityId,
        priorityName: pri.priorityName || pri.priorityCode || 'Unknown',
        priorityCode: pri.priorityCode,
        slaHours: pri.slaHours,
        colorCode: pri.colorCode,
        displayOrder: pri.displayOrder,
        isActive: pri.isActive !== undefined ? pri.isActive : true
      }));
      
      return {
        data: {
          success: true,
          message: response?.message || 'Priorities retrieved successfully',
          data: transformedPriorities
        }
      };
    } catch (error) {
      console.error('Error fetching priorities from backend:', error);
      // In production, don't fallback to mock - throw error
      if (process.env.NODE_ENV === 'production') {
        throw error;
      }
      // In development, fallback to mock if enabled
      console.warn('API failed, falling back to mock data:', error);
      try {
        const mockResponse = await MockDataService.getPriorities();
        if (mockResponse.success && mockResponse.data) {
          const priorities = Array.isArray(mockResponse.data) ? mockResponse.data : [];
          const transformedPriorities = priorities.map((pri, index) => {
            let priorityId = pri.priorityId || pri.id;
            if (typeof priorityId === 'string' && priorityId.startsWith('priority_')) {
              priorityId = parseInt(priorityId.replace('priority_', '')) || (index + 1);
            } else if (typeof priorityId !== 'number') {
              priorityId = index + 1;
            }
            return {
              priorityId: priorityId,
              priorityName: pri.name || pri.priorityName || 'Medium',
              priorityCode: pri.code || pri.priorityCode || 'medium',
              level: pri.level || 3,
              color: pri.color || '#d97706',
              ...pri
            };
          });
          
          return {
            data: {
              success: true,
              message: mockResponse.message || 'Priorities retrieved successfully',
              data: transformedPriorities
            }
          };
        }
      } catch (mockError) {
        console.error('Mock data also failed:', mockError);
      }
      throw error;
    }
  },
  
  /**
   * Get ticket statuses
   * GET /gateway/api/ticket/statuses
   */
  getTicketStatuses: async () => {
    const response = await ticketGet('/gateway/api/ticket/statuses');
    return wrapResponse(response);
  },
  
  /**
   * Get ticket tags
   * GET /gateway/api/ticket/tags
   */
  getTicketTags: async () => {
    const response = await ticketGet('/gateway/api/ticket/tags');
    return wrapResponse(response);
  },
  
  /**
   * Get SLA status for a ticket
   * GET /gateway/api/ticket/{ticketId}/sla-status
   */
  getSlaStatus: async (ticketId) => {
    try {
      const response = await ticketGet(`/gateway/api/ticket/${ticketId}/sla-status`);
      return wrapResponse(response);
    } catch (error) {
      console.error('Failed to fetch SLA status:', error);
      throw error;
    }
  },

  /**
   * Attempt scheduler-equivalent SLA / Matrix auto-escalation when calendar due_date is reached.
   * POST /gateway/api/ticket/{ticketId}/sla/try-auto-escalate
   */
  tryAutoEscalateSlaIfDue: async (ticketId) => {
    const response = await ticketPost(
      `/gateway/api/ticket/${ticketId}/sla/try-auto-escalate`,
      {}
    );
    return wrapResponse(response);
  },

  savetickettimespent: async (userId, ticketId, timespent) => {
    try {
      const payload = {
        userId: userId,
        ticketId: ticketId,
        timespent: timespent
      };
      const response = await ticketPost(`/gateway/registration/api/signup/organization/ticket-save-spent-time`, payload);
      return wrapResponse(response);
    } catch (error) {
      console.error('Error saving time spent:', error);
      throw error;
    }
  },

  /**
   * Get time spent by ticket ID (agent-wise aggregated)
   * GET /gateway/registration/api/signup/organization/ticket-time-spent/{ticketId}
   */
  getTimeSpentByTicketId: async (ticketId) => {
    try {
      const response = await ticketGet(`/gateway/registration/api/signup/organization/ticket-time-spent/${ticketId}`);
      return wrapResponse(response);
    } catch (error) {
      console.error('Error fetching time spent:', error);
      throw error;
    }
  },

  // ========== FEEDBACK OPERATIONS ==========
  
  /**
   * Submit feedback for a ticket
   * POST /gateway/api/ticket/feedback
   */
  submitFeedback: async (feedbackData) => {
    try {
      const response = await ticketPost('/gateway/api/ticket/feedback', feedbackData);
      return wrapResponse(response);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      throw error;
    }
  },

  /**
   * Get feedback for a ticket
   * GET /gateway/api/ticket/feedback/{ticketId}
   */
  getFeedback: async (ticketId) => {
    try {
      const response = await ticketGet(`/gateway/api/ticket/feedback/${ticketId}`);
      return wrapResponse(response);
    } catch (error) {
      console.error('Error fetching feedback:', error);
      throw error;
    }
  },

  /**
   * Get closed tickets for the current agent with pagination
   * GET /gateway/api/ticket/my-tickets/closed
   */
  getClosedTickets: async (params = {}) => {
    try {
      const { page = 0, size = 10, agentId, search, priorityId, startDate, endDate, statusCode } = params;
      let url = `/gateway/api/ticket/my-tickets/closed?page=${page}&size=${size}`;
      if (agentId) {
        url += `&agentId=${agentId}`;
      }
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      if (priorityId) {
        url += `&priorityId=${priorityId}`;
      }
      if (startDate) {
        url += `&startDate=${encodeURIComponent(startDate)}`;
      }
      if (endDate) {
        url += `&endDate=${encodeURIComponent(endDate)}`;
      }
      if (statusCode) {
        url += `&statusCode=${encodeURIComponent(statusCode)}`;
      }
      const response = await ticketGet(url);
      return wrapResponse(response);
    } catch (error) {
      console.error('Error fetching closed tickets:', error);
      throw error;
    }
  },

  /**
   * Get team tickets for Team Lead
   * GET /gateway/api/ticket/team-tickets
   * Returns all tickets assigned to any agent within the Team Lead's team
   */
  getTeamTickets: async (params = {}) => {
    try {
      const {
        page = 0,
        size = 10,
        search,
        assigneeId,
        status,
        inquiryCategoryId,
        inquiryPriorityLevelId,
      } = params;
      let url = `/gateway/api/ticket/team-tickets?page=${page}&size=${size}`;
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      if (assigneeId !== null && assigneeId !== undefined && assigneeId !== 'all') {
        url += `&assigneeId=${assigneeId}`;
      }
      if (status !== null && status !== undefined && status !== 'all') {
        url += `&status=${encodeURIComponent(status)}`;
      }
      if (inquiryCategoryId != null && inquiryCategoryId !== '' && inquiryCategoryId !== 'all') {
        url += `&inquiryCategoryId=${inquiryCategoryId}`;
      }
      if (inquiryPriorityLevelId != null && inquiryPriorityLevelId !== '' && inquiryPriorityLevelId !== 'all') {
        url += `&inquiryPriorityLevelId=${inquiryPriorityLevelId}`;
      }
      const response = await ticketGet(url);
      return wrapResponse(response);
    } catch (error) {
      console.error('Error fetching team tickets:', error);
      throw error;
    }
  },
  
  /**
   * Get all agents in the Team Lead's team
   * GET /gateway/api/ticket/team-agents
   * Returns all active agents in the Team Lead's team
   */
  getTeamAgents: async () => {
    try {
      const response = await ticketGet(`/gateway/api/ticket/team-agents`);
      return wrapResponse(response);
    } catch (error) {
      console.error('Error fetching team agents:', error);
      throw error;
    }
  },
  
  /**
   * Get team statistics for Team Lead dashboard
   * GET /gateway/api/ticket/team/stats
   * Returns counts for total tickets, active tickets, resolved today, and SLA compliance
   */
  getTeamStats: async () => {
    try {
      const response = await ticketGet(`/gateway/api/ticket/team/stats`);
      return wrapResponse(response);
    } catch (error) {
      console.error('Error fetching team stats:', error);
      throw error;
    }
  },
  
  /**
   * Count escalated tickets for Team Lead's team
   * GET /gateway/api/ticket/team/escalated/count
   */
  countTeamEscalatedTickets: async (agentId = null) => {
    try {
      let url = `/gateway/api/ticket/team/escalated/count`;
      if (agentId) {
        url += `?agentId=${agentId}`;
      }
      const response = await ticketGet(url);
      return wrapResponse(response);
    } catch (error) {
      console.error('Error fetching escalated tickets count:', error);
      throw error;
    }
  },
  
  /**
   * Get list of escalated tickets for Team Lead's team
   * GET /gateway/api/ticket/team/escalated/list
   */
  getTeamEscalatedTicketsList: async (params = {}) => {
    try {
      const { page = 0, size = 10, agentId, search, priorityId, startDate, endDate } = params;
      let url = `/gateway/api/ticket/team/escalated/list?page=${page}&size=${size}`;
      if (agentId) {
        url += `&agentId=${agentId}`;
      }
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      if (priorityId) {
        url += `&priorityId=${priorityId}`;
      }
      if (startDate) {
        url += `&startDate=${encodeURIComponent(startDate)}`;
      }
      if (endDate) {
        url += `&endDate=${encodeURIComponent(endDate)}`;
      }
      const response = await ticketGet(url);
      return wrapResponse(response);
    } catch (error) {
      console.error('Error fetching escalated tickets list:', error);
      throw error;
    }
  },
  
  /**
   * Count closed tickets for Team Lead's team
   * GET /gateway/api/ticket/team/closed/count
   */
  countTeamClosedTickets: async (agentId = null) => {
    try {
      let url = `/gateway/api/ticket/team/closed/count`;
      if (agentId) {
        url += `?agentId=${agentId}`;
      }
      const response = await ticketGet(url);
      return wrapResponse(response);
    } catch (error) {
      console.error('Error fetching closed tickets count:', error);
      throw error;
    }
  },
  
  /**
   * Get list of closed tickets for Team Lead's team
   * GET /gateway/api/ticket/team/closed/list
   */
  getTeamClosedTicketsList: async (params = {}) => {
    try {
      const { page = 0, size = 10, agentId, search, priorityId, startDate, endDate, statusCode } = params;
      let url = `/gateway/api/ticket/team/closed/list?page=${page}&size=${size}`;
      if (agentId) {
        url += `&agentId=${agentId}`;
      }
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      if (priorityId) {
        url += `&priorityId=${priorityId}`;
      }
      if (startDate) {
        url += `&startDate=${encodeURIComponent(startDate)}`;
      }
      if (endDate) {
        url += `&endDate=${encodeURIComponent(endDate)}`;
      }
      if (statusCode) {
        url += `&statusCode=${encodeURIComponent(statusCode)}`;
      }
      const response = await ticketGet(url);
      return wrapResponse(response);
    } catch (error) {
      console.error('Error fetching closed tickets list:', error);
      throw error;
    }
  },

  /**
   * Get closed tickets without feedback for the current user
   * GET /gateway/api/ticket/feedback/closed-without-feedback
   */
  getClosedTicketsWithoutFeedback: async () => {
    try {
      const response = await ticketGet('/gateway/api/ticket/feedback/closed-without-feedback');
      return wrapResponse(response);
    } catch (error) {
      console.error('Error fetching closed tickets without feedback:', error);
      throw error;
    }
  },

  /**
   * Get team average feedback for Team Lead
   * GET /gateway/api/ticket/team/{teamId}/average-feedback
   */
  getTeamAverageFeedback: async (teamId) => {
    try {
      const response = await ticketGet(`/gateway/api/ticket/team/${teamId}/average-feedback`);
      return wrapResponse(response);
    } catch (error) {
      console.error('Error fetching team average feedback:', error);
      throw error;
    }
  },

  /**
   * Get pending team lead assignments
   * GET /gateway/api/ticket/pending-team-lead-assignments?page=0&size=10&priorityId=1&escalatedByAgentId=123&search=term&sortBy=created
   */
  getPendingTeamLeadAssignments: async (params = {}) => {
    try {
      const { 
        page = 0, 
        size = 10, 
        priorityId = null, 
        escalatedByAgentId = null, 
        search = null, 
        sortBy = null 
      } = params;
      
      let queryParams = `page=${page}&size=${size}`;
      if (priorityId !== null && priorityId !== undefined && priorityId !== 'all') {
        queryParams += `&priorityId=${priorityId}`;
      }
      if (escalatedByAgentId !== null && escalatedByAgentId !== undefined && escalatedByAgentId !== 'all') {
        queryParams += `&escalatedByAgentId=${escalatedByAgentId}`;
      }
      if (search !== null && search !== undefined && search.trim() !== '') {
        queryParams += `&search=${encodeURIComponent(search.trim())}`;
      }
      if (sortBy !== null && sortBy !== undefined && sortBy !== '') {
        queryParams += `&sortBy=${encodeURIComponent(sortBy)}`;
      }
      
      const response = await ticketGet(`/gateway/api/ticket/pending-team-lead-assignments?${queryParams}`);
      return wrapResponse(response);
    } catch (error) {
      console.error('Error fetching pending team lead assignments:', error);
      throw error;
    }
  },

  // ========================================================================
  // PARENT-CHILD RELATIONSHIPS (V1) — feature-flagged via /config/features
  // ========================================================================
  //
  // All seven endpoints under /api/ticket/{ticketId}/relations/* return HTTP 404 when the
  // backend feature flag `app.features.ticket-relations.enabled=false`. Callers should
  // gate the *call site* on `useTicketRelationsEnabled()` so the user never sees a fake
  // 404 in their network tab. These methods deliberately do NOT swallow the 404 — they
  // surface like any other backend error so misuse is easy to spot in dev tools.

  /**
   * GET /gateway/api/ticket/{ticketId}/relations
   * Returns the bundle: { currentTicketId, currentTicketRole, parent, children, childCount }.
   */
  getRelationsBundle: async (ticketId) => {
    const response = await ticketGet(`/gateway/api/ticket/${ticketId}/relations`);
    return wrapResponse(response);
  },

  /**
   * GET /gateway/api/ticket/{ticketId}/relations/parent
   * Returns { hasParent, parent }.
   */
  getRelationParent: async (ticketId) => {
    const response = await ticketGet(`/gateway/api/ticket/${ticketId}/relations/parent`);
    return wrapResponse(response);
  },

  /**
   * GET /gateway/api/ticket/{ticketId}/relations/children
   * Returns { children: [...], count }.
   */
  getRelationChildren: async (ticketId) => {
    const response = await ticketGet(`/gateway/api/ticket/${ticketId}/relations/children`);
    return wrapResponse(response);
  },

  /**
   * GET /gateway/api/ticket/{ticketId}/relations/search-linkable?query=&page=&size=
   * Returns ResponseBean data: { items, page, size, totalElements, totalPages, first, last }.
   * Optional `signal` cancels an in-flight search (debounced typeahead).
   */
  searchLinkableRelationTickets: async (parentTicketId, { query = '', page = 0, size = 20, signal } = {}) => {
    const safePage = Math.max(0, Number(page) || 0);
    const safeSize = Math.max(1, Math.min(Number(size) || 20, 50));
    const response = await ticketGet(
      `/gateway/api/ticket/${parentTicketId}/relations/search-linkable`,
      { query, page: safePage, size: safeSize },
      signal ? { signal } : {},
    );
    return wrapResponse(response);
  },

  /**
   * GET /gateway/api/ticket/{canonicalTicketId}/relations/search-linkable-duplicates
   * D7 duplicate link search — no parent/child nesting filters (attachDuplicate validates).
   */
  searchLinkableDuplicateTickets: async (
    canonicalTicketId,
    { query = '', page = 0, size = 20, excludeDuplicateChildBlockedStatuses = false, signal } = {},
  ) => {
    const safePage = Math.max(0, Number(page) || 0);
    const safeSize = Math.max(1, Math.min(Number(size) || 20, 50));
    const response = await ticketGet(
      `/gateway/api/ticket/${canonicalTicketId}/relations/search-linkable-duplicates`,
      {
        query,
        page: safePage,
        size: safeSize,
        excludeDuplicateChildBlockedStatuses: Boolean(excludeDuplicateChildBlockedStatuses),
      },
      signal ? { signal } : {},
    );
    return wrapResponse(response);
  },

  /**
   * POST /gateway/api/ticket/{ticketId}/relations/attach-child
   * Body: { childTicketId } OR { childTicketNumber } — exactly one. Prefer childTicketId
   * from searchLinkableRelationTickets; childTicketNumber is for exact legacy flows.
   */
  /**
   * POST /gateway/api/ticket/{canonicalTicketId}/relations/attach-duplicate
   * Body: { duplicateTicketId }. Canonical = original; duplicate = marked copy.
   */
  attachDuplicate: async (canonicalTicketId, duplicateTicketId) => {
    const response = await ticketPost(
      `/gateway/api/ticket/${canonicalTicketId}/relations/attach-duplicate`,
      { duplicateTicketId: Number(duplicateTicketId) },
    );
    return wrapResponse(response);
  },

  /**
   * C2 portal: customer confirms newly created ticket is duplicate_of canonical.
   * POST /gateway/api/ticket/{duplicateTicketId}/relations/confirm-duplicate
   * Body: { canonicalTicketId }
   */
  confirmPortalDuplicate: async (duplicateTicketId, canonicalTicketId) => {
    const response = await ticketPost(
      `/gateway/api/ticket/${duplicateTicketId}/relations/confirm-duplicate`,
      { canonicalTicketId: Number(canonicalTicketId) },
    );
    return wrapResponse(response);
  },

  /**
   * Close a confirmed duplicate_of child ticket (canonical unchanged).
   * POST /gateway/api/ticket/{ticketId}/close-as-duplicate
   */
  closeAsDuplicate: async (ticketId, reason) => {
    const response = await ticketPost(
      `/gateway/api/ticket/${ticketId}/close-as-duplicate`,
      { reason: String(reason).trim() },
    );
    return wrapResponse(response);
  },

  attachExistingChild: async (parentTicketId, { childTicketId, childTicketNumber }) => {
    const body = {};
    if (childTicketId !== undefined && childTicketId !== null && childTicketId !== '') {
      body.childTicketId = Number(childTicketId);
    } else if (childTicketNumber !== undefined && childTicketNumber !== null) {
      body.childTicketNumber = String(childTicketNumber).trim();
    }
    const response = await ticketPost(
      `/gateway/api/ticket/${parentTicketId}/relations/attach-child`,
      body,
    );
    return wrapResponse(response);
  },

  /**
   * POST /gateway/api/ticket/{ticketId}/relations/create-child
   * Body: full TicketRequest (subject, description, priorityId, categoryId, …). The
   * backend orchestrates createTicket + attach in one transaction; response is
   * { child: TicketResponse, relation: RelationResponse }.
   */
  createChildTicket: async (parentTicketId, ticketRequest) => {
    const response = await ticketPost(
      `/gateway/api/ticket/${parentTicketId}/relations/create-child`,
      ticketRequest,
    );
    return wrapResponse(response);
  },

  /**
   * DELETE /gateway/api/ticket/{ticketId}/relations/{relationId}
   * Soft-unlinks the relation. The {ticketId} must be one of the two endpoints of the
   * relation (parent or child); the backend rejects with 409 otherwise.
   */
  unlinkRelation: async (ticketId, relationId) => {
    const response = await ticketDelete(
      `/gateway/api/ticket/${ticketId}/relations/${relationId}`,
    );
    return wrapResponse(response);
  },

  /**
   * GET /gateway/api/ticket/{ticketId}/relations/history?page=&size=
   * Paginated audit trail including soft-deleted (unlinked) rows.
   */
  getRelationHistory: async (ticketId, { page = 0, size = 20 } = {}) => {
    const safePage = Math.max(0, Number(page) || 0);
    const safeSize = Math.max(1, Math.min(Number(size) || 20, 100));
    const response = await ticketGet(
      `/gateway/api/ticket/${ticketId}/relations/history?page=${safePage}&size=${safeSize}`,
    );
    return wrapResponse(response);
  },

  /**
   * POST /gateway/api/ticket/relations/badges
   * Batched parent/child badge lookup for listing UIs (TicketQueue, dashboards). Returns
   * { ticketsAsParent: { id: { childrenCount } }, ticketsAsChild: { id: { parentTicketId, parentTicketNumber, relationId } } }.
   * Tickets that are neither parent nor child are absent from both maps; the UI treats
   * absence as "no badge". Backend caps the batch at 500 ids.
   */
  getRelationBadges: async (ticketIds) => {
    const ids = Array.isArray(ticketIds)
      ? ticketIds.map((x) => Number(x)).filter((x) => Number.isFinite(x))
      : [];
    if (ids.length === 0) {
      // Avoid a network round-trip when the caller has no ids; return an empty payload
      // shaped exactly like the real one so callers don't need a special case.
      return { data: { success: true, data: { ticketsAsParent: {}, ticketsAsChild: {} } } };
    }
    const response = await ticketPost(
      `/gateway/api/ticket/relations/badges`,
      { ticketIds: ids },
    );
    return wrapResponse(response);
  },

  /**
   * D6 — Bulk ticket import (JSON rows; Excel parsed client-side).
   * POST /gateway/api/ticket/bulk-import
   */
  bulkImportTickets: async ({ rows, originalFileName }) => {
    const response = await ticketPost('/gateway/api/ticket/bulk-import', {
      rows,
      originalFileName: originalFileName || null,
    });
    return wrapResponse(response);
  },

  /**
   * D6 — CSV template from server (optional; requires bulk_ticket_import can_view).
   */
  downloadBulkImportTemplateCsv: async () => {
    const { ticketGet } = await import('../services/apiClient');
    return ticketGet('/gateway/api/ticket/bulk-import/template');
  },
};


export default ticketService;
